import { FieldProps } from "@/core/components/Field";
import FlightPathBanner from "@/components/partials/FlightPathBanner";
import {
  CALCULATE_DELAY,
  FieldValues,
  FlightProblems,
  FlightSearchResult,
  SelectedTransport,
  HeadingLevel,
  Stopover,
  TransportType,
  SelectedFlight,
  FlightDataEntry,
} from "@/core/types/types";
import getFullAlternativeRoute from "@/utils/getFullAlternativeRoute";
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { DescriptionalSelectField } from "./DescriptionalSelectField";
import { useFormContext } from "react-hook-form";
import FlightDelayDateTimeField from "../FlightDelayDateTimeField/FlightDelayDateTimeField";
import { flightApi } from "@/api/flightApi";
import { PreselectedAlertComponent } from "./PreselectedAlertComponent";
import getPreselectedFlightProblems from "@/components/custom/SelectMultiLegProblem/getPreselectedFlightProblems";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";

export interface PreselectedValue {
  flightProblem?: string;
  arrivalTime?: string;
}

const SelectMultiLegProblem = ({ field }: FieldProps) => {
  const [flightData, setFlightData] = useState<Record<string, FlightDataEntry>>(
    {}
  );
  const [preselectedValues, setPreselectedValues] = useState<
    Record<string, PreselectedValue>
  >({});
  const { variables } = useClaimCheck();
  const [loading, setLoading] = useState<boolean>(true);

  const flightProblemsOptions = Object.values(FlightProblems)
    .filter((problem) => problem !== FlightProblems.Other)
    .map((problem) => ({
      label: t(`flightProblem.${problem.toLowerCase()}`),
      value: problem,
    }));
  const { getValues } = useFormContext();

  const formValues = getValues();

  const singleLegOptions = flightProblemsOptions.filter(
    (option) =>
      option.value !== FlightProblems.OnTime &&
      option.value !== FlightProblems.MissedConnection
  );
  const optionsWithoutMissedConnection = flightProblemsOptions.filter(
    (option) => option.value !== FlightProblems.MissedConnection
  );

  const {
    originIATA,
    destinationIATA,
    flightStopovers,
    journeyType,
    flightsArrivalTimes,
    flights,
    originallySelectedFlightProblem,
  } = variables;

  const transformedData = useMemo(() => {
    return flightStopovers?.map((item: Stopover) => ({
      name: item.name,
      iata: item.iata,
      mode: TransportType.plane,
    }));
  }, [flightStopovers]);

  const alternativeRouteArray = getFullAlternativeRoute(
    originIATA,
    destinationIATA,
    TransportType.plane,
    transformedData
  );

  const getRouteKey = (departureIata: string, arrivalIata: string): string =>
    `${departureIata},${arrivalIata}`;

  const isMissedConnection = (
    currentFlightSearchResult?: FlightSearchResult,
    currentSelectedFlight?: SelectedFlight,
    previousFlightSearchResult?: FlightSearchResult
  ): boolean => {
    const arrivalTime = previousFlightSearchResult?.actualArrival;
    const departureTime =
      currentFlightSearchResult?.actualDeparture ??
      currentSelectedFlight?.scheduledDepartureDateTime;

    if (!arrivalTime || !departureTime) return false;

    return new Date(arrivalTime).getTime() > new Date(departureTime).getTime();
  };

  const processFlightData = async () => {
    const flightApiPromises: Promise<FlightSearchResult>[] = flights.map(
      (flight: SelectedTransport) =>
        flightApi
          .searchFlights({
            departureDate: flight.scheduledDepartureDateTime.split("T")[0],
            originAirport: flight.departureAirportIata,
            destinationAirport: flight.arrivalAirportIata,
          })
          .then((searchResults: FlightSearchResult[]) => {
            const filteredResults = searchResults.filter(
              (result: FlightSearchResult) =>
                result.operatingAirlineCode + result.flightNumber ===
                  flight.flightNumber ||
                result.flightNumber === flight.flightNumber
            );
            return filteredResults[0];
          })
    );
    const flightApiResults: FlightSearchResult[] = await Promise.all(
      flightApiPromises
    );

    const flightApiResultPerRoute: Record<string, FlightDataEntry> =
      flightApiResults.reduce(
        (
          resultsPerRoute: Record<string, FlightDataEntry>,
          result: FlightSearchResult,
          index: number
        ) => {
          const flight: SelectedFlight = flights[index];
          const routeKey: string = getRouteKey(
            flight.departureAirportIata,
            flight.arrivalAirportIata
          );
          const connectionIsMissed: boolean = isMissedConnection(
            result,
            flight,
            flightApiResults[index - 1]
          );
          resultsPerRoute[routeKey] = {
            flightSearchResult: result,
            connectionIsMissed,
          };
          return resultsPerRoute;
        },
        {}
      );

    setFlightData(flightApiResultPerRoute);
    setLoading(false);
  };

  useEffect(() => {
    if (flights) {
      processFlightData();
    }
  }, [flights]);

  useEffect(() => {
    const flightProblems = getPreselectedFlightProblems(
      flightData,
      journeyType,
      originallySelectedFlightProblem
    );
    setPreselectedValues(flightProblems);
  }, [flightData]);

  return (
    <>
      {!loading ? (
        alternativeRouteArray?.map((route, index) => {
          const departureFromRoute = route.departure?.iata;
          const arrivalFromRoute = route.arrival?.iata;
          const departureStopover =
            departureFromRoute !== originIATA.iata
              ? index.toString()
              : undefined;
          const arrivalStopover =
            arrivalFromRoute !== destinationIATA.iata
              ? (index + 1).toString()
              : undefined;
          const options: FieldValues[] =
            alternativeRouteArray.length > 1
              ? !index
                ? optionsWithoutMissedConnection
                : flightProblemsOptions
              : singleLegOptions;
          const fieldKey: string = getRouteKey(
            departureFromRoute,
            arrivalFromRoute
          );

          const preselectedValue: PreselectedValue =
            preselectedValues[fieldKey];
          const arrivalTimeDefaultValue = preselectedValue?.arrivalTime;
          const preselectedFlightProblem = preselectedValue?.flightProblem;
          const flightDataEntry = flightData[fieldKey];

          return (
            <Card
              sx={{
                boxShadow: "none",
                padding: 2,
                marginTop: index > 0 ? 2 : 0,
              }}
              key={route.arrival.iata + index}
            >
              <FlightPathBanner
                originAirportData={route.departure}
                destinationAirportData={route.arrival}
                departureStopoverOrder={departureStopover}
                arrivalStopoverOrder={arrivalStopover}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ my: 2 }}>
                <PreselectedAlertComponent
                  field={field}
                  preselectedValue={preselectedFlightProblem}
                  flightDataEntry={flightDataEntry}
                  selectedStatus={formValues[fieldKey]}
                />
              </Box>
              <DescriptionalSelectField
                id={field.id}
                label="label.flightProblem"
                controllerName={fieldKey}
                options={options}
                initialValue={preselectedFlightProblem}
              />
              {formValues[fieldKey] === FlightProblems.Delayed ? (
                <Box mt={2}>
                  <Typography variant={HeadingLevel.Body1}>
                    {t("actualArrivalDate.label")}
                  </Typography>
                  <FlightDelayDateTimeField
                    field={{
                      ...field,
                      label: "actualArrivalDateTime.label",
                      key: `manualActualArrivalDateTime[${index}]`,
                      id: `manualActualArrivalDateTime[${index}]`,
                      validate: {
                        required: true,
                      },
                      defaultValue: arrivalTimeDefaultValue,
                      properties: {
                        errorTranslationKey: "manualActualArrivalDateTime",
                        [CALCULATE_DELAY]: `${flightsArrivalTimes[index].scheduledArrivalTime}:00`,
                      },
                    }}
                  />
                </Box>
              ) : null}
            </Card>
          );
        })
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default SelectMultiLegProblem;
